import { useProjectStore } from "@/stores/project";
import { useEquipmentStore } from "@/stores/equipment";
import type { ProjectDevice, PanelDesign } from "@/types/projectTypes";
import { log } from "console";

export const annual_ratio_for_finance = 4.9; // Annual ratio % used in financial calculations

/**
 * Calculates the total capacity of the solar system in kW
 * @returns Total capacity in kilowatts
 */
export const totalPower = (): number => {
  const projectStore = useProjectStore();
  const equipmentStore = useEquipmentStore();

  let totalPowerInWatt = 0;

  // Get all panel devices from the project
  const panelDevices =
    projectStore.getCurrentProject?.devices.filter(
      (device: ProjectDevice) => device.device_type === "Panel"
    ) || [];

  // Calculate total power for each panel
  panelDevices.forEach((device: ProjectDevice) => {
    const panel = equipmentStore.panels.find((p) => p.id === device.device_id);
    if (panel) {
      totalPowerInWatt += panel.power * device.pcs;
    }
  });
  console.log("Total power:", totalPowerInWatt);
  return totalPowerInWatt / 1000;
};

/**
 * Calculates the total battery capacity in kWh
 * @returns Total battery capacity in kilowatt-hours
 */
export const totalBattery = (): number => {
  const projectStore = useProjectStore();
  const equipmentStore = useEquipmentStore();

  let totalBatteryInkWh = 0;

  // Get all battery devices from the project
  const batteryDevices =
    projectStore.getCurrentProject?.devices.filter(
      (device: ProjectDevice) => device.device_type === "Battery"
    ) || [];

  // Calculate total battery capacity for each battery
  batteryDevices.forEach((device: ProjectDevice) => {
    const battery = equipmentStore.batteries.find(
      (b) => b.id === device.device_id
    );
    if (battery) {
      totalBatteryInkWh += battery.nominal_capacity * device.pcs;
    }
  });

  console.log("Total battery:", totalBatteryInkWh);
  return Math.round(totalBatteryInkWh/100)/10;
};

/**
 * Calculates the total price of all devices in the project
 * @returns Total price of devices in Euro
 */
export const totalDevicesPrice = (): number => {
  const projectStore = useProjectStore();
  const equipmentStore = useEquipmentStore();

  let totalPrice = 0;

  // Get all devices from the project
  const devices = projectStore.getCurrentProject?.devices || [];

  // Calculate total price for each device
  devices.forEach((device: ProjectDevice) => {
    let devicePrice = 0;

    // Declare variables outside switch
    const panel = equipmentStore.panels.find((p) => p.id === device.device_id);
    const inverter = equipmentStore.inverters.find(
      (i) => i.id === device.device_id
    );
    const battery = equipmentStore.batteries.find(
      (b) => b.id === device.device_id
    );
    const evCharger = equipmentStore.evChargers.find(
      (e) => e.id === device.device_id
    );
    const meter = equipmentStore.meters.find((m) => m.id === device.device_id);

    // Find the device in the appropriate equipment store based on type
    switch (device.device_type) {
      case "Panel":
        devicePrice = Number(panel?.price || 0);
        break;
      case "Inverter":
        devicePrice = Number(inverter?.price || 0);
        break;
      case "Battery":
        devicePrice = Number(battery?.price || 0);
        break;
      case "EVCharger":
        devicePrice = Number(evCharger?.price || 0);
        break;
      case "Meter":
        devicePrice = Number(meter?.price || 0);
        break;
    }

    // Ensure pcs is a number
    const quantity = Number(device.pcs || 0);
    const itemTotal = devicePrice * quantity;

    console.log(
      `Device price for ${device.device_type}: ${devicePrice} x ${quantity} = ${itemTotal}`
    );

    // Add to total price (price * quantity)
    totalPrice += itemTotal;
  });

  console.log(`Total devices price: ${totalPrice}`);
  return totalPrice;
};

/**
 * Calculates the total price of all services in the project
 * @returns Total price of services in Euro
 */
export const totalServicesPrice = (): number => {
  const projectStore = useProjectStore();
  const equipmentStore = useEquipmentStore();

  let totalPrice = 0;
  const project = projectStore.getCurrentProject;

  if (!project) return 0;

  const totalKWPannels = project.total_power || 0;
  const serviceType = project.service_type;

  console.log(`
  === SERVICE PRICE CALCULATION ===
  Service Type: ${serviceType}
  Total Panel Power: ${totalKWPannels} kW
  `);

  // Get all devices to count batteries and EV chargers
  const devices = project.devices || [];
  const panelCount = devices
    .filter((d) => d.device_type === "Panel")
    .reduce((sum, d) => sum + d.pcs, 0);
  const inverterCount = devices
    .filter((d) => d.device_type === "Inverter")
    .reduce((sum, d) => sum + d.pcs, 0);
  const batteryCount = devices
    .filter((d) => d.device_type === "Battery")
    .reduce((sum, d) => sum + d.pcs, 0);
  const evChargerCount = devices
    .filter((d) => d.device_type === "EVCharger")
    .reduce((sum, d) => sum + d.pcs, 0);
  const meterCount = devices
    .filter((d) => d.device_type === "Meter")
    .reduce((sum, d) => sum + d.pcs, 0);

  console.log(`Battery Count: ${batteryCount}`);
  console.log(`EV Charger Count: ${evChargerCount}`);
  console.log(`Meter Count: ${meterCount}`);
  console.log(`Panel Count: ${panelCount}`);
  console.log(`Inverter Count: ${inverterCount}`);

  // Get service prices from equipment store
  const services = equipmentStore.services;

  console.log(`Available services: ${services.length}`);
  services.forEach((service) => {
    console.log(`Service: ${service.name}, Price: ${service.price}`);
  });

  // Helper function to get service price by name
  const getServicePrice = (name: string): number => {
    const service = services.find((s) => s.name === name);
    const price = Number(service?.price || 0);
    console.log(`Service ${name}: ${price}€`);
    return price;
  };

  // Add inverter communication cost for product_supply_commissioning, full_service or complete_solution
  if (
    serviceType === "product_supply_commissioning" ||
    serviceType === "full_service" ||
    serviceType === "complete_solution"
  ) {
    if (inverterCount > 0) {
      console.log(`Adding inverter communication cost for ${serviceType}`);
      const invComServiceName = "INT-COM-INV";
      const invComPrice = getServicePrice(invComServiceName);
    
      totalPrice += invComPrice;
      if (inverterCount > 1) {
        const invComServiceName = "INT-COM-INV-1";
        const invComPrice = getServicePrice(invComServiceName);
        totalPrice += (invComPrice * (inverterCount - 1));
      }
      console.log(
        `Added inverter communication: ${invComServiceName} = ${invComPrice}€`
      );
      console.log(`Running total: ${totalPrice}€`);
    }
    if (evChargerCount > 0 && inverterCount > 0) {
      console.log(`Adding EV charger communication cost for ${serviceType}`);
      const evComServiceName = "INT-COM-EVC";
      const evComPrice = getServicePrice(evComServiceName);
      totalPrice += (evComPrice * evChargerCount);
    }
    //TODO: add INT-COM-ABOX cost for heat pump interface installation if it exist between meters
    console.log(`Running total: ${totalPrice}€`);
  }

  // Add connection cost for full_service, or complete_solution
  if (
    serviceType === "full_service" ||
    serviceType === "complete_solution"
  ) {
    console.log(`Adding connection cost for ${serviceType}`);
    let connectionServiceName = "";
    let connectionPrice = 0;

    if (totalKWPannels > 0 && totalKWPannels <= 6) {
      connectionServiceName = "PV-PRJ-CNCT-0-6";
      connectionPrice = getServicePrice(connectionServiceName);
      totalPrice += connectionPrice;
    } else if (totalKWPannels > 6 && totalKWPannels <= 20) {
      connectionServiceName = "PV-PRJ-CNCT-6-20";
      connectionPrice = getServicePrice(connectionServiceName);
      totalPrice += connectionPrice;
    } else if (totalKWPannels > 20 && totalKWPannels <= 50) {
      connectionServiceName = "PV-PRJ-CNCT-20-50";
      connectionPrice = getServicePrice(connectionServiceName);
      totalPrice += connectionPrice;
    } else if (totalKWPannels > 50 && totalKWPannels <= 100) {
      connectionServiceName = "PV-PRJ-CNCT-50-100";
      connectionPrice = getServicePrice(connectionServiceName);
      totalPrice += connectionPrice;
    }
    // new Certificazione della taratura cost for >11.08 kW
    if (totalKWPannels > 11.08) {
      connectionServiceName = "PV-PRJ-SPI-CLB";
      connectionPrice = getServicePrice(connectionServiceName);
      totalPrice += connectionPrice;
    }
    console.log(
      `Added connection cost: ${connectionServiceName} = ${connectionPrice}€`
    );
    console.log(`Running total: ${totalPrice}€`);
  }

  

  // Add installation cost for complete_solution
  if (serviceType === "complete_solution") {
    console.log(`Adding installation costs for complete_solution`);

    // Installation cost based on total kW of panels
    let installServiceName = "";
    let installPricePerKW = 0;
    let totalInstallPrice = 0;

    if (totalKWPannels > 0 && totalKWPannels <= 6) {
      installServiceName = "INT-MAN-PV-0-6";
      installPricePerKW = getServicePrice(installServiceName);
      totalInstallPrice = installPricePerKW * totalKWPannels;
      totalPrice += totalInstallPrice;
    } else if (totalKWPannels > 6 && totalKWPannels <= 11) {
      installServiceName = "INT-MAN-PV-6-11";
      installPricePerKW = getServicePrice(installServiceName);
      totalInstallPrice = installPricePerKW * totalKWPannels;
      totalPrice += totalInstallPrice;
    } else if (totalKWPannels > 11 && totalKWPannels <= 20) {
      installServiceName = "INT-MAN-PV-11-20";
      installPricePerKW = getServicePrice(installServiceName);
      totalInstallPrice = installPricePerKW * totalKWPannels;
      totalPrice += totalInstallPrice;
    } else if (totalKWPannels > 20 && totalKWPannels <= 100) {
      installServiceName = "INT-MAN-PV-20-100";
      installPricePerKW = getServicePrice(installServiceName);
      totalInstallPrice = installPricePerKW * totalKWPannels;
      totalPrice += totalInstallPrice;
    } else if (totalKWPannels > 100 && totalKWPannels <= 500) {
      installServiceName = "INT-MAN-PV-100-500";
      installPricePerKW = getServicePrice(installServiceName);
      totalInstallPrice = installPricePerKW * totalKWPannels;
      totalPrice += totalInstallPrice;
    } else if (totalKWPannels > 500 && totalKWPannels <= 1000) {
      installServiceName = "INT-MAN-PV-500-1000";
      installPricePerKW = getServicePrice(installServiceName);
      totalInstallPrice = installPricePerKW * totalKWPannels;
      totalPrice += totalInstallPrice;
    }
    console.log(
      `Added panel installation: ${installServiceName} x ${totalKWPannels}kW = ${totalInstallPrice}€ (${installPricePerKW}€ per kW)`
    );
    console.log(`Running total: ${totalPrice}€`);

    // Add battery installation cost
    if (batteryCount > 0) {
      const baseBatteryServiceName = "INT-MAN-PV-BAT";
      const additionalBatteryServiceName = "INT-MAN-PV-BAT-1";

      const baseBatteryPrice = getServicePrice(baseBatteryServiceName);
      const additionalBatteryPrice = getServicePrice(
        additionalBatteryServiceName
      );

      let totalBatteryInstallPrice = baseBatteryPrice;
      totalPrice += baseBatteryPrice;

      console.log(
        `Added base battery installation: ${baseBatteryServiceName} = ${baseBatteryPrice}€`
      );

      if (batteryCount > 3) {
        const extraBatteries = batteryCount - 3;
        const extraBatteriesPrice = additionalBatteryPrice * extraBatteries;
        totalBatteryInstallPrice += extraBatteriesPrice;
        totalPrice += extraBatteriesPrice;
        console.log(
          `Added extra battery installation: ${additionalBatteryServiceName} x ${extraBatteries} = ${extraBatteriesPrice}€`
        );
      }

      console.log(`Total battery installation: ${totalBatteryInstallPrice}€`);
      console.log(`Running total: ${totalPrice}€`);
    }

    // Add EV charger installation cost
    if (evChargerCount > 0) {
      const evChargerServiceName = "INT-MAN-PV-EVC";
      const evChargerPricePerUnit = getServicePrice(evChargerServiceName);
      const totalEVChargerPrice = evChargerPricePerUnit * evChargerCount;

      totalPrice += totalEVChargerPrice;
      console.log(
        `Added EV charger installation: ${evChargerServiceName} x ${evChargerCount} = ${totalEVChargerPrice}€`
      );
      console.log(`Running total: ${totalPrice}€`);
    }

    //TODO: add INT-MAN-PV-ABOX cost for heat pump interface installation if it exist between meters
  }

  console.log(`=== FINAL SERVICE PRICE: ${totalPrice}€ ===`);
  return Number(totalPrice);
};

/**
 * Calculates the total price of the system in Euro
 * @returns Total price in Euro
 */
export const totalPrice = (): number => {
  const projectStore = useProjectStore();
  const devicesPrice = Number(totalDevicesPrice());
  const servicesPrice = Number(totalServicesPrice());

  // Get client type from project
  const clientType = projectStore.getCurrentProject?.client_type;
  const serviceType = projectStore.getCurrentProject?.service_type;
  // Apply multiplier based on client type
  let multiplier = 1; // Default no discount
  let discountPercentage = 0; // Default no discount
  
  switch (clientType) {
    case "distributor":
      multiplier = 0.5535; // 44.65% discount
      discountPercentage = 0.4465; // 44.65% discount
      break;
    case "installer":
      multiplier = 0.675; // 32.5% discount
      discountPercentage = 0.325; // 32.5% discount
      break;
    case "standard":
    default:
      // For standard clients, use the user-selected discount percentage
      discountPercentage = projectStore.getCurrentProject?.financial_data?.discount_percentage || 0;
      multiplier = 1 - discountPercentage;
      break;
  }
  let vat = 0.22;
  let adjustedServicesPrice = servicesPrice;
  if (serviceType === "complete_solution") {
    vat = 0.1;
    // Calculate adjusted services price first
    adjustedServicesPrice = servicesPrice * multiplier;
  }

  // Calculate adjusted device price first
  const adjustedDevicesPrice = devicesPrice * multiplier;
  

  // Only update project data if project exists
  if (projectStore.project) {
    projectStore.$patch((state) => {
      if (state.project && state.project.financial_data) {
        state.project.financial_data.vat_percentage = vat;
        state.project.financial_data.vat_amount =
          Math.round((adjustedDevicesPrice + adjustedServicesPrice) * vat * 100) / 100;
        // Store the calculated discount percentage for all client types
        state.project.financial_data.discount_percentage = discountPercentage;
      }
    });
  }

  //const finalPrice = (adjustedDevicesPrice + servicesPrice) * (1 + vat);
  const finalPrice = (adjustedDevicesPrice + adjustedServicesPrice) ;

  console.log(`
  Devices price: ${devicesPrice}
  Client type: ${clientType}
  Multiplier: ${multiplier}
  Adjusted devices price: ${adjustedDevicesPrice}
  Adjusted services price: ${adjustedServicesPrice}
  Final total price: ${finalPrice}
  `);

  // Apply multiplier only to devices price, not services
  return finalPrice;
};

/**
 * Calculates the monthly installment amount in Euro
 * @param months Number of months for the installment plan
 * @returns Monthly payment amount in Euro
 */
export const monthlyInstallment = (): number => {
  const projectStore = useProjectStore();
  const serviceType = projectStore.getCurrentProject?.service_type;
  let vat = 0.22;
  if (serviceType === "complete_solution") {
    vat = 0.1;
  }
  const price: number = totalPrice() * (1 + vat) || 0;
  const installmentRatio =
    projectStore.getCurrentProject?.financial_data?.installment_ratio ||
    annual_ratio_for_finance;
  const months =
    projectStore.getCurrentProject?.financial_data
      ?.installment_duration_months || 120;

  // Calculate monthly interest rate from annual ratio
  const monthlyRate = installmentRatio / 12 / 100;

  // Calculate monthly payment using loan amortization formula
  const monthlyPayment =
    (price * monthlyRate * Math.pow(1 + monthlyRate, months)) /
    (Math.pow(1 + monthlyRate, months) - 1);
  console.log("Monthly payment:", monthlyPayment);
  return Math.round(monthlyPayment);
};

/**
 * Calculates the Performance Ratio based on location
 * @returns Performance Ratio in MWh/kWp/year
 */
export const coefficient = async (): Promise<number> => {
  const projectStore = useProjectStore();
  const mapState = projectStore.getCurrentProject?.map_state;

  if (!mapState?.center) {
    console.warn("No location data available");
    return 1.3; // Default value for North Italy
  }

  const { lat, lng } = mapState.center;

  try {
    // Import the API client
    const { default: api } = await import("@/plugins/api");

    // Make request to Solar API via backend proxy
    const response = await api.get('/google-maps/solar/', {
      params: { lat, lng }
    });

    // Axios response structure is different from fetch
    const data = response.data;
    // Check if we have solar potential data
    if (!data.solarPotential) {
      console.warn("No solar potential data in API response");
      return 1.3;
    }

    console.log("Full solar potential data:", data.solarPotential);

    // Get sunshine hours from the API response
    const sunshineHours = data.solarPotential.maxSunshineHoursPerYear;

    if (sunshineHours) {
      const systemEfficiency = 1;

      // Calculate expected output in kWh for a 1kW system
      const expectedOutputKWh = sunshineHours * systemEfficiency;

      // Convert to MWh for the coefficient
      let solarCoefficient = expectedOutputKWh / 1000;
      if (solarCoefficient < 1.2) {
        solarCoefficient = 1.2;
      }
      if (solarCoefficient > 1.4) {
        solarCoefficient = 1.4;
      }
      console.log(
        `Calculated performance ratio: ${solarCoefficient.toFixed(
          3
        )} MWh/kWp/year`
      );

      return solarCoefficient;
    }
    // Fallback default
    console.log("Using fallback default performance ratio: 1.3 MWh/kWp/year");
    return 1.3;
  } catch (error) {
    console.error("Error fetching solar data:", error);
    return 1.3; // Default value for North Italy
  }
};

/**
 * Calculates the annual energy production in kWh
 * @returns Annual production in kilowatt-hours
 */
export const annualProduction = async (): Promise<number> => {
  const solarCoefficient: number = (await coefficient()) || 1.3;
  const projectStore = useProjectStore();
  const equipmentStore = useEquipmentStore();

  let totalPowerInWattOriented = 0;

  // Get all panel devices from the project
  const panelDevices =
    projectStore.getCurrentProject?.drawing_data.panels || [];

  console.log("=== Annual Production Calculation Debug ===");
  console.log("Panel devices from drawing_data:", panelDevices);
  console.log("Project devices:", projectStore.getCurrentProject?.devices);
  console.log("Equipment store panels:", equipmentStore.panels.length);

  // If no panels are positioned on the roof, fall back to project devices
  if (panelDevices.length === 0) {
    console.log("No panels positioned on roof, using project devices as fallback");
    const projectPanelDevices = projectStore.getCurrentProject?.devices.filter(
      (device: ProjectDevice) => device.device_type === "Panel"
    ) || [];

    projectPanelDevices.forEach((device: ProjectDevice) => {
      const panel = equipmentStore.panels.find((p) => p.id === device.device_id);
      if (panel && device.pcs) {
        // Use default orientation (South - optimal) and slope (16 degrees) for fallback
        const panelOrientation = 180; // South orientation
        const panelSlope = 16; // Default slope

        // Find the matching coefficient direction based on panel slope
        const matchingCoefficientDirection =
          equipmentStore.coefficientDirections.find((cd) => {
            const [min, max] = cd.gradian_range.split("-").map(Number);
            return panelSlope >= min && panelSlope <= max;
          });

        if (!matchingCoefficientDirection) {
          console.warn(`No coefficient direction found for slope ${panelSlope}`);
          return;
        }

        // Use South orientation coefficient (optimal for Northern Hemisphere)
        const orientationCoefficient = matchingCoefficientDirection.south;

        console.log(
          `Fallback calculation - Panel: ${panel.model_number}, Quantity: ${device.pcs}, Power: ${panel.power}W, Orientation: South, Coefficient: ${orientationCoefficient}`
        );

        const powerOriented = panel.power * device.pcs * orientationCoefficient;
        totalPowerInWattOriented += powerOriented;
      }
    });
  } else {
    // Calculate total power for each positioned panel
    console.log("Using positioned panels for calculation");
    panelDevices.forEach((device: PanelDesign) => {
      console.log("Processing panel:", device);
      const panel = equipmentStore.panels.find((p) => p.id === device.model_id);
      console.log("Found panel model:", panel);
      if (panel) {
        const panelOrientation = device.properties.orientation || 0;
        const panelSlope = device.properties.slope?.value || 16;
        console.log(`Panel ${panel.model_number}: orientation=${panelOrientation}°, slope=${panelSlope}°`);
        console.log("Available coefficient directions:", equipmentStore.coefficientDirections);

        // Find the matching coefficient direction based on panel slope
        const matchingCoefficientDirection =
          equipmentStore.coefficientDirections.find((cd) => {
            const [min, max] = cd.gradian_range.split("-").map(Number);
            console.log(`Checking range ${min}-${max} for slope ${panelSlope}`);
            return panelSlope >= min && panelSlope <= max;
          });

        if (!matchingCoefficientDirection) {
          console.warn(`No coefficient direction found for slope ${panelSlope}, using default coefficient`);
          // Use a default coefficient of 1.0 if no matching direction is found
          const powerOriented = panel.power * 1.0;
          totalPowerInWattOriented += powerOriented;
          return;
        }

        const normalizedOrientation = Math.round(panelOrientation % 360);
        let orientationCoefficient = 1;

        // Get the appropriate coefficient based on orientation
        switch (true) {
          case normalizedOrientation >= 338 || normalizedOrientation < 23:
            orientationCoefficient = matchingCoefficientDirection.north;
            break;
          case normalizedOrientation >= 23 && normalizedOrientation < 68:
            orientationCoefficient = matchingCoefficientDirection.north_east;
            break;
          case normalizedOrientation >= 68 && normalizedOrientation < 113:
            orientationCoefficient = matchingCoefficientDirection.east;
            break;
          case normalizedOrientation >= 113 && normalizedOrientation < 158:
            orientationCoefficient = matchingCoefficientDirection.south_east;
            break;
          case normalizedOrientation >= 158 && normalizedOrientation < 203:
            orientationCoefficient = matchingCoefficientDirection.south;
            break;
          case normalizedOrientation >= 203 && normalizedOrientation < 248:
            orientationCoefficient = matchingCoefficientDirection.south_west;
            break;
          case normalizedOrientation >= 248 && normalizedOrientation < 293:
            orientationCoefficient = matchingCoefficientDirection.west;
            break;
          case normalizedOrientation >= 293 && normalizedOrientation < 338:
            orientationCoefficient = matchingCoefficientDirection.north_west;
            break;
        }

        console.log(
          `Panel slope: ${panelSlope}, Orientation: ${normalizedOrientation}, Coefficient: ${orientationCoefficient}`
        );

        const powerOriented = panel.power * orientationCoefficient;
        totalPowerInWattOriented += powerOriented;
      }
    });
  }

  const annualProduction: number = totalPowerInWattOriented * solarCoefficient;
  console.log("Annual production:", annualProduction);
  return Math.round(annualProduction);
};

/**
 * Calculates the annual revenue in Euro
 * @returns Annual revenue in Euro
 */
export const annualRevenue = async (): Promise<number> => {
  const projectStore = useProjectStore();
  const project = projectStore.getCurrentProject;
  const kWh_import_price_in_euro =
    projectStore.project?.financial_data.import_energy_price || 0.22;
  const kWh_export_price_in_euro =
    projectStore.project?.financial_data.export_energy_price || 0.10;

  if (!project) return 0;

  const production: number = (await annualProduction()) || 0;
  const consumptionGrandTotal: number =
    project.consumption_data?.grand_total || 0;

  // Calculate revenue from consumption coverage
  const consumptionCoverage = Math.min(production, consumptionGrandTotal);
  const consumptionRevenue = consumptionCoverage * kWh_import_price_in_euro;

  // Calculate revenue from excess production
  const excessProduction = Math.max(0, production - consumptionGrandTotal);
  const excessRevenue = excessProduction * kWh_export_price_in_euro;

  // Total annual revenue
  console.log("Consumption revenue:", consumptionRevenue);
  console.log("Excess revenue:", excessRevenue);
  return consumptionRevenue + excessRevenue;
};

/**
 * Calculates the cash flow chart data based on payment type
 * @returns Array of 26 values representing cash flow over 25 years
 */
export const cashFlowChartData = async (): Promise<number[]> => {
  console.log("=== cashFlowChartData called ===");
  console.log("=== cashFlowChartData called - START ===");
  const projectStore = useProjectStore();
  console.log("Project exists:", !!projectStore.getCurrentProject);
  console.log("Financial data exists:", !!projectStore.getCurrentProject?.financial_data);
  const serviceType = projectStore.getCurrentProject?.service_type;
  
  // Ensure financial_data exists with default values
  if (!projectStore.getCurrentProject?.financial_data) {
    console.log("No financial data found, using default values");
  }
  let vat = 0.22;
  if (serviceType === "complete_solution") {
    vat = 0.1;
  }

  // Calculate monthly installment first to ensure we have the latest value
  const monthlyPayment = monthlyInstallment();
  const installmentAmountYearly = monthlyPayment * 12;

  const installmentDurationYears =
    (projectStore.project?.financial_data?.installment_duration_months || 120) /
    12;
  const annualRevenue = projectStore.project?.financial_data?.annual_revenue || 0;
  const finalPrice = projectStore.project?.financial_data?.final_price || 0;
  const annualCashback = ((projectStore.project?.financial_data?.cashback_amount) || 0)/10;
  const annualAssurance = 120;
  const renewInverterCost = 2000;
  const includeRenewInverter =
    projectStore.project?.financial_data?.include_renew_inverter || false;

  console.log("Cash Flow Input Values:", {
    installmentDurationYears,
    annualRevenue,
    installmentAmountYearly,
    finalPrice,
    annualCashback,
    annualAssurance,
    renewInverterCost,
    includeRenewInverter,
    paymentType: projectStore.project?.financial_data.payment_type,
    installment_duration_months:
      projectStore.project?.financial_data.installment_duration_months,
    installment_ratio: projectStore.project?.financial_data.installment_ratio,
    monthlyPayment,
  });

  // Initialize array with 26 zeros
  const cashFlowData = new Array(26).fill(0);

  if (projectStore.project?.financial_data?.payment_type === "one_time") {
    // For one-time payment, first year is -finalPrice, then each year adds monthlySaving * 12
    cashFlowData[0] = -(finalPrice || 0);
    let i = 1;
    for (i; i <= 10; i++) {
      const previousValue = cashFlowData[i - 1];
      const newValue = previousValue + (annualRevenue || 0) + annualCashback - annualAssurance;
      cashFlowData[i] = newValue;
      
      if (i <= 3) {
        console.log(`Year ${i}: ${previousValue} + ${annualRevenue || 0} + ${annualCashback} - ${annualAssurance} = ${newValue}`);
      }
    }

    for (i; i < 26; i++) {
      cashFlowData[i] =
        cashFlowData[i - 1] + (annualRevenue || 0) - annualAssurance;
      if (i === 11 && includeRenewInverter) {
        cashFlowData[i] = cashFlowData[i] - renewInverterCost;
      }
    }
  } else if (
    projectStore.project?.financial_data.payment_type === "installment"
  ) {
    // For installment payment, first year is 0, then each year adds (monthlySaving * 12) - (monthlyPayment * 12)
    cashFlowData[0] = 0;
    let i = 1;
    for (i; i <= installmentDurationYears; i++) {
      cashFlowData[i] =
        cashFlowData[i - 1] +
        (annualRevenue || 0) +
        annualCashback -
        annualAssurance -
        installmentAmountYearly;
    }
    for (i; i <= 10; i++) {
      cashFlowData[i] =
        cashFlowData[i - 1] +
        (annualRevenue || 0) +
        annualCashback -
        annualAssurance;
    }

    for (i; i < 26; i++) {
      cashFlowData[i] =
        cashFlowData[i - 1] + (annualRevenue || 0) - annualAssurance;
      if (i === 11 && includeRenewInverter) {
        cashFlowData[i] = cashFlowData[i] - renewInverterCost;
      }
    }
  }

  console.log("Cash Flow Output:", cashFlowData);
  console.log("Cash Flow Debug - First 5 years:", cashFlowData.slice(0, 5));
  console.log("Cash Flow Debug - annualAssurance used:", annualAssurance);
  console.log("Cash Flow Debug - annualCashback used:", annualCashback);
  return cashFlowData;
};

/**
 * Calculates the cashback amount based on cashback percentage
 * @returns Cashback amount in Euro
 */
export const calculateCashbackAmount = (): number => {
  const projectStore = useProjectStore();
  const cashbackPercentage = projectStore.getCurrentProject?.financial_data?.cashback_percentage || 0;
  const basePrice = totalPrice(); // Use the total price as base value
  const vatPercentage = projectStore.getCurrentProject?.financial_data?.vat_percentage || 0.22;
  
  const cashbackAmount = (cashbackPercentage / 100) * (basePrice * (1 + vatPercentage));
  
  console.log("Cashback Calculation:", {
    cashbackPercentage,
    basePrice,
    vatPercentage,
    cashbackAmount
  });
  
  return Math.round(cashbackAmount * 100) / 100; // Round to 2 decimal places
};
